import React, { useEffect, useMemo, useRef } from 'react'
import clsx from 'clsx'
import { useStreams } from '../app/StreamsProvider.jsx'
import { NotificationTypes, useNotifications } from '../hooks/useNotifications.jsx'
import MetricsTrendChart from './MetricsTrendChart.jsx'
import '../activity-stream/styles.css'
import '../styles/metrics.css'

const numberFormatter = new Intl.NumberFormat('en-US')

const formatNumber = (value) => {
  if (value === null || value === undefined) {
    return '—'
  }
  return numberFormatter.format(value)
}

const formatRelativeTime = (timestamp) => {
  if (!timestamp) {
    return 'Never'
  }
  const diff = Date.now() - timestamp
  if (diff < 1_000) {
    return 'Just now'
  }
  const seconds = Math.floor(diff / 1_000)
  if (seconds < 60) {
    return `${seconds}s ago`
  }
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes}m ago`
  }
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `${hours}h ago`
  }
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

const MetricsPanel = () => {
  const { metricsStream } = useStreams()
  const {
    currentMetrics,
    connectionState,
    error,
    lastUpdatedAt,
    trend,
    retry,
    isStreaming,
    isAuthenticated,
  } = metricsStream

  const { notify } = useNotifications()
  const lastErrorRef = useRef(null)
  const lastStatusRef = useRef(null)

  // Extract metrics from current data
  const metrics = currentMetrics ?? {}
  const statements = metrics.statementMetrics ?? []

  useEffect(() => {
    if (!error) {
      lastErrorRef.current = null
      return
    }
    const key = `${error.status ?? 'na'}-${error.message ?? 'error'}`
    if (lastErrorRef.current === key) {
      return
    }
    lastErrorRef.current = key
    notify({
      type: error.status === 401 || error.status === 403
        ? NotificationTypes.ERROR
        : NotificationTypes.WARNING,
      title: 'Metrics stream interrupted',
      message: error.message ?? 'Connection closed unexpectedly. Retry when ready.',
      autoDismissMs: 8_000,
    })
  }, [error, notify])

  useEffect(() => {
    if (connectionState === lastStatusRef.current) {
      return
    }
    lastStatusRef.current = connectionState

    if (connectionState === 'connected') {
      notify({
        type: NotificationTypes.SUCCESS,
        title: 'Metrics stream connected',
        message: 'Real-time metrics updates every 5 seconds.',
        autoDismissMs: 4_000,
      })
    }
  }, [connectionState, notify])

  const isLoading = connectionState === 'connecting' || connectionState === 'reconnecting'
  const isConnected = connectionState === 'connected'
  const hasMetrics = Boolean(currentMetrics)

  const trendPoints = useMemo(() => {
    if (!trend?.length) {
      return []
    }
    return trend.slice(-8).map((sample) => sample.inputCountDelta)
  }, [trend])

  const statusLabel = useMemo(() => {
    switch (connectionState) {
      case 'connected':
        return 'Connected'
      case 'connecting':
        return 'Connecting…'
      case 'reconnecting':
        return 'Reconnecting…'
      case 'error':
        return 'Error'
      case 'stopped':
        return 'Stopped'
      default:
        return 'Idle'
    }
  }, [connectionState])

  if (!isAuthenticated) {
    return (
      <div className="as-app">
        <main className="as-layout" style={{ display: 'block' }}>
          <section className="as-panel metrics-panel">
            <p style={{ margin: 0, textAlign: 'center' }}>Authenticate to begin streaming metrics.</p>
          </section>
        </main>
      </div>
    )
  }

  if (isLoading && !hasMetrics) {
    return (
      <div className="as-app">
        <main className="as-layout" style={{ display: 'block' }}>
          <section className="as-panel metrics-panel metrics-panel--loading">
            <div className="metrics-panel__loader" aria-live="assertive">
              Connecting to metrics stream…
            </div>
          </section>
        </main>
      </div>
    )
  }

  if (connectionState === 'error' && !hasMetrics) {
    return (
      <div className="as-app">
        <main className="as-layout" style={{ display: 'block' }}>
          <section className="as-panel metrics-panel metrics-panel--error">
            <div className="metrics-panel__error" role="alert">
              <h2>Metrics unavailable</h2>
              <p>{error?.message ?? 'Unable to retrieve metrics at this time.'}</p>
              <button type="button" onClick={retry}>Try again</button>
            </div>
          </section>
        </main>
      </div>
    )
  }

  if (!hasMetrics) {
    return (
      <div className="as-app">
        <main className="as-layout" style={{ display: 'block' }}>
          <section className="as-panel metrics-panel metrics-panel--empty">
            <div className="metrics-panel__empty" role="status">
              <h2>No metrics reported yet</h2>
              <p>Waiting for CEP runtime to publish metrics…</p>
            </div>
          </section>
        </main>
      </div>
    )
  }

  return (
    <div className="as-app">
      <main className="as-layout" style={{ display: 'block' }}>
        <section className="as-panel metrics-panel">
          <div className="metrics-panel__header">
            <h3>Runtime Metrics - {metrics.runtimeURI ?? 'Unknown'}</h3>
            <div className="metrics-panel__header-info">
              <span>Last updated {formatRelativeTime(lastUpdatedAt)}</span>
            </div>
          </div>

          {/* Metrics Dashboard */}
          <div className="metrics-totals">
            <div className="metrics-total-card">
              <div className="metrics-total-card__label">Total Inputs</div>
              <div className="metrics-total-card__value">{formatNumber(metrics.inputCount)}</div>
            </div>
            <div className="metrics-total-card">
              <div className="metrics-total-card__label">Input Delta</div>
              <div className="metrics-total-card__value">{formatNumber(metrics.inputCountDelta)}</div>
            </div>
            <div className="metrics-total-card">
              <div className="metrics-total-card__label">Throughput</div>
              <div className="metrics-total-card__value">{formatNumber((metrics.inputCountDelta ?? 0) / 5)}/s</div>
            </div>
            <div className="metrics-total-card">
              <div className="metrics-total-card__label">Schedule Depth</div>
              <div className="metrics-total-card__value">{formatNumber(metrics.scheduleDepth)}</div>
            </div>
            <div className="metrics-total-card">
              <div className="metrics-total-card__label">Active Statements</div>
              <div className="metrics-total-card__value">{statements.length}</div>
            </div>
          </div>

          {/* Trend Chart */}
          <div className="metrics-chart-container">
            <MetricsTrendChart trend={trend} runtimeURI={metrics.runtimeURI} />
          </div>

          <section className="metrics-panel__detail">
            <header className="metrics-panel__detail-header">
              <h3>Statement Metrics</h3>
            </header>

            {statements.length ? (
              <div className="metrics-panel__table-wrapper">
                <table className="metrics-panel__table">
                  <thead>
                    <tr>
                      <th scope="col">Statement</th>
                      <th scope="col">Deployment</th>
                      <th scope="col">Inputs</th>
                      <th scope="col">Outputs (I)</th>
                      <th scope="col">Outputs (R)</th>
                      <th scope="col">CPU time</th>
                      <th scope="col">Wall time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statements.map((statement) => (
                      <tr key={`${statement.deploymentId}-${statement.statementName}`}>
                        <th scope="row">{statement.statementName}</th>
                        <td>{statement.deploymentId ?? '—'}</td>
                        <td>{formatNumber(statement.numInput)}</td>
                        <td>{formatNumber(statement.numOutputIStream)}</td>
                        <td>{formatNumber(statement.numOutputRStream)}</td>
                        <td>{formatNumber(statement.cpuTime)}</td>
                        <td>{formatNumber(statement.wallTime)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {!statements.length ? (
              <div className="metrics-panel__empty" role="status">
                <p>No statement metrics reported for this runtime yet.</p>
              </div>
            ) : null}
          </section>
        </section>
      </main>
    </div>
  )
}

export default MetricsPanel
